/// Image cache for the game.
///
/// Flame resolves images asynchronously inside each component's onLoad. There
/// are only 26 sprites, so the web port preloads the lot up front instead,
/// which keeps every component constructor synchronous and removes an entire
/// class of half-initialised-sprite bugs.

const assetRoot = '/assets/images';

const cache = new Map<string, HTMLImageElement>();

function loadImage(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`assets: failed to load ${path}`));
    image.src = `${assetRoot}/${path}`;
  });
}

export async function preloadImages(paths: readonly string[]): Promise<void> {
  const missing = paths.filter((path) => !cache.has(path));
  const loaded = await Promise.all(missing.map(loadImage));
  missing.forEach((path, index) => cache.set(path, loaded[index]));
}

/** Throws rather than returning a blank sprite, so a typo fails loudly. */
export function imageFromCache(path: string): HTMLImageElement {
  const image = cache.get(path);
  if (!image) {
    throw new Error(`assets: ${path} was not preloaded`);
  }
  return image;
}

export function hasImage(path: string): boolean {
  return cache.has(path);
}

/** Every sprite the game can show, in either theme. */
export const gameImagePaths: readonly string[] = [
  'new/dino/idle/dark.png',
  'new/dino/idle/light.png',
  'new/dino/run/dark.png',
  'new/dino/run/light.png',
  'new/dino/duck/dark.png',
  'new/dino/duck/light.png',
  'new/dino/dead/dark.png',
  'new/dino/dead/light.png',
  'new/ground/dark.png',
  'new/ground/light.png',
  'new/cactus/small/dark.png',
  'new/cactus/small/light.png',
  'new/cactus/large/dark.png',
  'new/cactus/large/light.png',
  'new/cactus/mixed/dark.png',
  'new/cactus/mixed/light.png',
  'new/pterodactyl/dark.png',
  'new/pterodactyl/light.png',
  'new/moon/moon_phase.png',
  'new/star/star.png',
  'new/star/star_bright.png',
  'new/star/star_dim.png',
  'new/sun/sun.png',
  'new/cloud/cloud_1.png',
  'new/cloud/cloud_2.png',
  'new/cloud/cloud_3.png',
];
